import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Bus, MapPin, Calendar, Users, Plus, Trash2, ArrowRight, ArrowLeft,
  Clock, Star, Zap, DollarSign, Award, Loader2, Ticket, Route
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const todayStr = () => new Date().toISOString().split('T')[0];

const fmtDuration = (mins) => {
  if (!mins) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

const BusMultiCityPlanner = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('plan'); // plan | results | review | done
  const [passengers, setPassengers] = useState(1);
  const [legs, setLegs] = useState([
    { origin: '', destination: '', date: todayStr() },
    { origin: '', destination: '', date: todayStr() },
  ]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [selected, setSelected] = useState({}); // legIndex -> option
  const [itinerary, setItinerary] = useState(null);
  const [paxDetails, setPaxDetails] = useState([]);
  const [bookingResult, setBookingResult] = useState(null);
  const [payProvider, setPayProvider] = useState('mtn');
  const [payNumber, setPayNumber] = useState('');
  const [payState, setPayState] = useState('idle'); // idle | pending | paid
  const [payInfo, setPayInfo] = useState(null);

  const payAllLegs = async () => {
    if (!payNumber) {
      toast({ title: 'Mobile number required', description: 'Enter the number to charge.', variant: 'destructive' });
      return;
    }
    setPayState('pending');
    try {
      const res = await api.post('/bus/payments/group/initiate', {
        booking_group_id: bookingResult.booking_group_id,
        payment_method: 'mobile_money',
        mobile_money_provider: payProvider,
        mobile_money_number: payNumber,
      });
      setPayInfo({ amount: res.data.amount, legs: res.data.legs });
      toast({ title: 'Approve on your phone', description: `Confirm the K${res.data.amount} ${payProvider.toUpperCase()} prompt.` });
      // Poll group status until settled
      const groupId = bookingResult.booking_group_id;
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const st = await api.get(`/bus/payments/group/status/${groupId}`);
          if (st.data.status === 'paid') {
            clearInterval(poll);
            setPayState('paid');
            toast({ title: 'Payment complete', description: 'All legs are paid and confirmed.' });
          } else if (tries > 25) {
            clearInterval(poll);
            setPayState('idle');
            toast({ title: 'Still pending', description: 'Payment not confirmed yet. Try again or check My Tickets.', variant: 'destructive' });
          }
        } catch (_) { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setPayState('idle');
      toast({ title: 'Payment failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const updateLeg = (idx, field, value) => {
    setLegs((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const addLeg = () => {
    if (legs.length >= 5) return;
    const last = legs[legs.length - 1];
    setLegs([...legs, { origin: last.destination || '', destination: '', date: last.date }]);
  };

  const removeLeg = (idx) => {
    if (legs.length <= 2) return;
    setLegs(legs.filter((_, i) => i !== idx));
  };

  const handleSearch = async () => {
    for (const l of legs) {
      if (!l.origin || !l.destination || !l.date) {
        toast({ title: 'Incomplete', description: 'Fill origin, destination and date for every leg.', variant: 'destructive' });
        return;
      }
    }
    setLoading(true);
    try {
      const res = await api.post('/bus/planner/search', { legs, passengers });
      setSearchData(res.data);
      setSelected({});
      setStep('results');
    } catch (e) {
      toast({ title: 'Search failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const applyRecommendation = (rec) => {
    const sel = {};
    rec.legs.forEach((l) => { sel[l.leg_index] = l.selected; });
    setSelected(sel);
    toast({ title: rec.label, description: 'Applied to your journey. Adjust any leg below if you like.' });
  };

  const selectOption = (legIndex, option) => {
    setSelected((prev) => ({ ...prev, [legIndex]: option }));
  };

  const allLegsSelected = searchData && searchData.legs.every((l) => selected[l.leg_index]);

  const handleCreateItinerary = async () => {
    const selections = Object.entries(selected).map(([legIndex, opt]) => ({
      leg_index: Number(legIndex),
      trip_id: opt.tripId,
      seat_class: 'standard',
    }));
    setLoading(true);
    try {
      const res = await api.post('/bus/planner/create-itinerary', { selections, passengers });
      setItinerary(res.data.itinerary);
      setPaxDetails(Array.from({ length: passengers }, () => ({ name: '', phone: '', id_number: '' })));
      setStep('review');
    } catch (e) {
      if ([401, 403].includes(e.response?.status)) {
        toast({ title: 'Login required', description: 'Please log in to save and book your journey.', variant: 'destructive' });
        navigate('/login');
        return;
      }
      toast({ title: 'Could not save plan', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updatePax = (i, field, value) => {
    setPaxDetails((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  };

  const handleBook = async () => {
    for (const p of paxDetails) {
      if (!p.name || !p.phone) {
        toast({ title: 'Passenger details', description: 'Name and phone are required for each passenger.', variant: 'destructive' });
        return;
      }
    }
    setLoading(true);
    try {
      const res = await api.post(`/bus/planner/itinerary/${itinerary.id}/book`, { passenger_details: paxDetails });
      setBookingResult(res.data);
      setStep('done');
    } catch (e) {
      toast({ title: 'Booking failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const recIcon = { fastest: Zap, cheapest: DollarSign, best_rated: Award };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/bus" data-testid="back-to-bus-search" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Route className="w-6 h-6 text-amber-400" />
          <div>
            <h1 className="text-lg font-bold leading-tight">Multi-City Trip Planner</h1>
            <p className="text-xs text-slate-400">Chain up to 5 bus legs into one journey</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* STEP: PLAN */}
        {step === 'plan' && (
          <div className="space-y-5" data-testid="planner-plan-step">
            <Card className="bg-slate-900 border-slate-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-400" /> Your legs
                </h2>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  <Input
                    type="number" min={1} max={10} value={passengers}
                    onChange={(e) => setPassengers(Math.max(1, Math.min(10, Number(e.target.value))))}
                    className="w-16 bg-slate-800 border-slate-700"
                    data-testid="passengers-input"
                  />
                  <span className="text-sm text-slate-400">pax</span>
                </div>
              </div>

              <div className="space-y-3">
                {legs.map((leg, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`leg-row-${idx}`}>
                    <div className="col-span-12 sm:col-span-3">
                      <Label className="text-xs text-slate-400">From</Label>
                      <Input value={leg.origin} placeholder="Lusaka"
                        onChange={(e) => updateLeg(idx, 'origin', e.target.value)}
                        className="bg-slate-800 border-slate-700" data-testid={`leg-origin-${idx}`} />
                    </div>
                    <div className="col-span-12 sm:col-span-3">
                      <Label className="text-xs text-slate-400">To</Label>
                      <Input value={leg.destination} placeholder="Ndola"
                        onChange={(e) => updateLeg(idx, 'destination', e.target.value)}
                        className="bg-slate-800 border-slate-700" data-testid={`leg-destination-${idx}`} />
                    </div>
                    <div className="col-span-9 sm:col-span-4">
                      <Label className="text-xs text-slate-400">Date</Label>
                      <Input type="date" value={leg.date} min={todayStr()}
                        onChange={(e) => updateLeg(idx, 'date', e.target.value)}
                        className="bg-slate-800 border-slate-700" data-testid={`leg-date-${idx}`} />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Button variant="ghost" disabled={legs.length <= 2}
                        onClick={() => removeLeg(idx)}
                        className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`remove-leg-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-4">
                <Button variant="outline" onClick={addLeg} disabled={legs.length >= 5}
                  className="border-slate-700 bg-transparent hover:bg-slate-800" data-testid="add-leg-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add leg
                </Button>
                <Button onClick={handleSearch} disabled={loading}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="planner-search-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bus className="w-4 h-4 mr-1" />}
                  Find connections
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* STEP: RESULTS */}
        {step === 'results' && searchData && (
          <div className="space-y-5" data-testid="planner-results-step">
            {/* Recommendations */}
            {searchData.recommendations?.[0]?.type !== 'incomplete' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {searchData.recommendations.map((rec) => {
                  const Icon = recIcon[rec.type] || Star;
                  return (
                    <Card key={rec.type} className="bg-slate-900 border-slate-800 p-4" data-testid={`rec-${rec.type}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-amber-400" />
                        <span className="font-semibold text-sm">{rec.label}</span>
                      </div>
                      <p className="text-lg font-bold">K{rec.total_price}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1 mb-3">
                        <Clock className="w-3 h-3" /> {fmtDuration(rec.total_duration_minutes)} travel
                      </p>
                      <Button size="sm" variant="outline" onClick={() => applyRecommendation(rec)}
                        className="w-full border-slate-700 bg-transparent hover:bg-slate-800"
                        data-testid={`apply-${rec.type}`}>
                        Apply
                      </Button>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Per-leg options */}
            {searchData.legs.map((leg) => (
              <Card key={leg.leg_index} className="bg-slate-900 border-slate-800 p-4" data-testid={`leg-options-${leg.leg_index}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs bg-slate-800 px-2 py-0.5 rounded">Leg {leg.leg_index + 1}</span>
                  <span className="font-semibold text-sm flex items-center gap-1">
                    {leg.origin} <ArrowRight className="w-3 h-3 text-slate-500" /> {leg.destination}
                  </span>
                  <span className="text-xs text-slate-400 ml-auto">{leg.date}</span>
                </div>

                {leg.options.length === 0 ? (
                  <p className="text-sm text-amber-400/80" data-testid={`leg-empty-${leg.leg_index}`}>{leg.message}</p>
                ) : (
                  <div className="space-y-2">
                    {leg.options.map((opt) => {
                      const isSel = selected[leg.leg_index]?.tripId === opt.tripId;
                      return (
                        <button key={opt.tripId} onClick={() => selectOption(leg.leg_index, opt)}
                          data-testid={`option-${leg.leg_index}-${opt.tripId}`}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            isSel ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 hover:border-slate-600 bg-slate-800/40'
                          }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{opt.operatorName}</p>
                              <p className="text-xs text-slate-400 flex items-center gap-2">
                                <Clock className="w-3 h-3" /> {opt.departureTime} → {opt.arrivalTime}
                                <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400" />{opt.operatorRating || '—'}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-amber-400">K{opt.pricing?.standard}</p>
                              <p className="text-xs text-slate-400">{opt.availableSeats} seats</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>
            ))}

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep('plan')}
                className="border-slate-700 bg-transparent hover:bg-slate-800" data-testid="edit-plan-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Edit legs
              </Button>
              <Button onClick={handleCreateItinerary} disabled={!allLegsSelected || loading}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="review-itinerary-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                Review itinerary
              </Button>
            </div>
          </div>
        )}

        {/* STEP: REVIEW */}
        {step === 'review' && itinerary && (
          <div className="space-y-5" data-testid="planner-review-step">
            <Card className="bg-slate-900 border-slate-800 p-5">
              <h2 className="text-base font-semibold mb-4">Your journey</h2>
              <div className="space-y-3">
                {itinerary.legs.map((leg, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 border border-slate-700">
                      <div>
                        <p className="text-sm font-medium">
                          {leg.route.origin?.city} → {leg.route.destination?.city}
                        </p>
                        <p className="text-xs text-slate-400">{leg.date} · {leg.departureTime} · {leg.operatorName}</p>
                      </div>
                      <p className="font-bold text-amber-400">K{leg.totalPrice}</p>
                    </div>
                    {itinerary.layovers[i] && (
                      <p className={`text-xs my-1 ml-3 ${itinerary.layovers[i].negative_connection ? 'text-red-400' : 'text-slate-400'}`}>
                        {itinerary.layovers[i].negative_connection
                          ? `⚠ Tight/negative connection in ${itinerary.layovers[i].city}`
                          : `${itinerary.layovers[i].layover_hours}h layover in ${itinerary.layovers[i].city}${itinerary.layovers[i].overnight ? ' (overnight)' : ''}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-800">
                <span className="text-sm text-slate-400">{passengers} passenger(s) · fares</span>
                <span className="text-xl font-bold text-amber-400" data-testid="itinerary-total">K{itinerary.totalPrice}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">+ a small per-leg processing fee is added at payment.</p>
            </Card>

            <Card className="bg-slate-900 border-slate-800 p-5">
              <h2 className="text-base font-semibold mb-4">Passenger details</h2>
              <div className="space-y-4">
                {paxDetails.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2" data-testid={`pax-row-${i}`}>
                    <div className="col-span-12 sm:col-span-4">
                      <Label className="text-xs text-slate-400">Full name</Label>
                      <Input value={p.name} onChange={(e) => updatePax(i, 'name', e.target.value)}
                        className="bg-slate-800 border-slate-700" data-testid={`pax-name-${i}`} />
                    </div>
                    <div className="col-span-12 sm:col-span-4">
                      <Label className="text-xs text-slate-400">Phone</Label>
                      <Input value={p.phone} onChange={(e) => updatePax(i, 'phone', e.target.value)}
                        className="bg-slate-800 border-slate-700" data-testid={`pax-phone-${i}`} />
                    </div>
                    <div className="col-span-12 sm:col-span-4">
                      <Label className="text-xs text-slate-400">NRC / ID (optional)</Label>
                      <Input value={p.id_number} onChange={(e) => updatePax(i, 'id_number', e.target.value)}
                        className="bg-slate-800 border-slate-700" data-testid={`pax-id-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep('results')}
                className="border-slate-700 bg-transparent hover:bg-slate-800" data-testid="back-to-results-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleBook} disabled={loading}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="book-itinerary-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ticket className="w-4 h-4 mr-1" />}
                Book all legs
              </Button>
            </div>
          </div>
        )}

        {/* STEP: DONE */}
        {step === 'done' && bookingResult && (
          <div className="space-y-5" data-testid="planner-done-step">
            <Card className="bg-slate-900 border-slate-800 p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <Ticket className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-lg font-bold">{bookingResult.message}</h2>
              <p className="text-sm text-slate-400 mt-1">Booking group: {bookingResult.booking_group_id.slice(0, 8)}</p>
              <p className="text-2xl font-bold text-amber-400 mt-3">K{bookingResult.total_amount}</p>
            </Card>

            {/* Single payment for the whole itinerary */}
            {payState !== 'paid' ? (
              <Card className="bg-slate-900 border-slate-800 p-5" data-testid="group-pay-card">
                <p className="text-sm font-semibold mb-1">Pay for all legs in one go</p>
                <p className="text-xs text-slate-400 mb-3">One Mobile Money charge of <span className="text-amber-400 font-bold">K{bookingResult.total_amount}</span> settles every leg{itinerary && Number(bookingResult.total_amount) > Number(itinerary.totalPrice) ? ` (fares K${itinerary.totalPrice} + processing fees K${(Number(bookingResult.total_amount) - Number(itinerary.totalPrice)).toFixed(0)})` : ''}.</p>
                <div className="flex gap-2 mb-3">
                  {['mtn', 'airtel'].map((p) => (
                    <button key={p} onClick={() => setPayProvider(p)} disabled={payState === 'pending'}
                      data-testid={`group-pay-provider-${p}`}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold border ${payProvider === p ? 'bg-amber-500 text-slate-950 border-amber-500' : 'border-slate-700 text-slate-300'}`}>
                      {p.toUpperCase()}
                    </button>
                  ))}
                </div>
                <Input value={payNumber} onChange={(e) => setPayNumber(e.target.value)} placeholder="Mobile money number e.g. +2609..."
                  disabled={payState === 'pending'} className="bg-slate-800 border-slate-700 mb-3" data-testid="group-pay-number" />
                <Button onClick={payAllLegs} disabled={payState === 'pending'}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="group-pay-btn">
                  {payState === 'pending'
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Awaiting confirmation…</>
                    : `Pay K${bookingResult.total_amount} now`}
                </Button>
              </Card>
            ) : (
              <Card className="bg-green-500/10 border-green-500/40 p-5 text-center" data-testid="group-pay-done">
                <p className="text-green-400 font-bold">✓ Itinerary paid in full</p>
                <p className="text-xs text-slate-400 mt-1">All {payInfo?.legs || bookingResult.bookings.length} legs confirmed.</p>
              </Card>
            )}
            <div className="space-y-2">
              {bookingResult.bookings.map((b) => (
                <Card key={b.id} className="bg-slate-900 border-slate-800 p-4 flex items-center justify-between" data-testid={`booked-leg-${b.legIndex}`}>
                  <div>
                    <p className="text-sm font-medium">{b.boarding?.city} → {b.alighting?.city}</p>
                    <p className="text-xs text-slate-400">{b.trip?.date} · {b.trip?.departureTime} · {b.operatorName}</p>
                  </div>
                  <span className="text-xs font-mono bg-slate-800 px-2 py-1 rounded">{b.ticketCode}</span>
                </Card>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/bus/tickets')} className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="view-tickets-btn">
                View my tickets
              </Button>
              <Button variant="outline" onClick={() => { setStep('plan'); setSearchData(null); setItinerary(null); setBookingResult(null); }}
                className="border-slate-700 bg-transparent hover:bg-slate-800" data-testid="plan-another-btn">
                Plan another
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusMultiCityPlanner;
